import api from './api';

// --- Types ---

export interface AdminPlanOption {
  value: number;
  label: string;
  code: string;
}

export interface CreateVirtualPaymentPayload {
  billingCycle: 'month' | 'year';
  isTrial?: boolean;
  daysUntilNextPayment?: number;
}

export interface VirtualPaymentResult {
  userId: number;
  subscriptionId: number;
  status: string;
  nextPaymentDate: string | null;
}

export interface AdminClientOption {
  value: number;
  label: string;
}

export interface ClientSubscriptionSummary {
  isActive: boolean;
  isPeriodPaid: boolean;
  isBillingExempt: boolean;
  status: string;
  plan: string | null;
  planCode: string | null;
  amount?: string | number;
  nextPaymentDate: string | null;
  lastPaymentDate?: string | null;
  isAutoRenewal: boolean;
  isTrial: boolean;
  billingCycle?: string;
  canUseTrial: boolean;
  currency?: string;
}

export type ConnectionStatus = 'active' | 'pending' | 'rejected' | 'terminated';
export type TelegramStatus = 'active' | 'pending' | 'NOT_CONNECTED' | 'blocked';

export interface ConnectedPerson {
  id: number;
  name: string;
  surname: string;
  avatar?: string;
  connectionStatus: ConnectionStatus;
}

export interface AdminUserSubscription {
  planName: string;
  planCode: string;
  status: string;
  provider?: 'wayforpay' | 'apple' | 'google' | 'virtual' | null;
  grantedBy?: 'payment' | 'admin_grant' | null;
}

export interface AdminClientUser {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  isEmailVerified: boolean;
  status: number; // 1 = активний, 2 = заблокований, 3 = видалений
  billingExempt: boolean;
  isProtectedDemoAccount: boolean;
  createdAt: string;
  timezone?: string | null;
  connectionsByStatus: Record<string, number>;
  subscription?: AdminUserSubscription | null;
  roles: string[];
}

export interface GetAdminClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  planCode?: string;
  status?: number;
}

export interface AdminClientsMeta {
  total: number;
  page: number;
  limit: number;
}

export interface AdminClientsResponse {
  data: AdminClientUser[];
  meta: AdminClientsMeta;
}

export interface AdminExpertUser {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  rating: number | null;
  specializations: string[];
  isEmailVerified: boolean;
  status: number; // 1 = активний, 2 = заблокований, 3 = видалений
  billingExempt: boolean;
  isProtectedDemoAccount: boolean;
  createdAt: string;
  timezone?: string | null;
  connectionsByStatus: Record<string, number>;
  telegramStatus: TelegramStatus;
  subscription?: AdminUserSubscription | null;
}

export interface AdminExpertsResponse {
  data: AdminExpertUser[];
  meta: AdminClientsMeta;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  isEmailVerified: boolean;
  status: number; // 1 = активний, 2 = заблокований, 3 = видалений
  createdAt: string;
  lastLoginAt?: string | null;
  timezone?: string | null;
  locale?: string | null;
  roles: string[];
  subscription?: AdminUserSubscription | null;
}

export interface GetAdminUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  emailVerified?: boolean;
  status?: number;
}

export interface AdminUsersResponse {
  data: AdminUser[];
  meta: AdminClientsMeta;
}

export interface AdminClientDetail {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  avatar?: string;
  gender?: string;
  bornYear?: number;
  status: number;
  billingExempt: boolean;
  isProtectedDemoAccount: boolean;
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  clientCode?: string;
  timezone?: string;
  locale?: string;
  connectedExperts: ConnectedPerson[];
}

export interface AdminExpertDetail {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  avatar?: string;
  gender?: string;
  bornYear?: number;
  rating: number | null;
  status: number;
  billingExempt: boolean;
  isProtectedDemoAccount: boolean;
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  specializations: string[];
  connectedClients: ConnectedPerson[];
  telegramStatus: TelegramStatus;
}

// --- Service ---

class AdminUsersService {
  async getClients(params: GetAdminClientsParams): Promise<AdminClientsResponse> {
    const response = await api.get<any>('/admin/clients', { params });
    const body = response.data;

    if (body && body.success && Array.isArray(body.data)) {
      return { data: body.data, meta: body.meta };
    }

    return { data: [], meta: { total: 0, page: 1, limit: params.limit ?? 25 } };
  }

  async getExperts(params: GetAdminClientsParams): Promise<AdminExpertsResponse> {
    const response = await api.get<any>('/admin/experts', { params });
    const body = response.data;

    if (body && body.success && Array.isArray(body.data)) {
      return { data: body.data, meta: body.meta };
    }

    return { data: [], meta: { total: 0, page: 1, limit: params.limit ?? 25 } };
  }

  async getUsers(params: GetAdminUsersParams): Promise<AdminUsersResponse> {
    const response = await api.get<any>('/admin/users', { params });
    const body = response.data;

    if (body && body.success && Array.isArray(body.data)) {
      return { data: body.data, meta: body.meta };
    }

    return { data: [], meta: { total: 0, page: 1, limit: params.limit ?? 25 } };
  }

  async updateUserRole(userId: number, role: string, active: boolean): Promise<string[]> {
    const response = await api.patch<any>(`/admin/users/${userId}/roles`, { role, active });
    return response.data.data?.roles ?? [];
  }

  /** Блокування (status=2) / розблокування (status=1) — окрема від видалення функція. */
  async setUserStatus(kind: 'clients' | 'experts', userId: number, status: 1 | 2): Promise<void> {
    await api.patch(`/admin/${kind}/${userId}/status`, { status });
  }

  async getClientById(id: number): Promise<AdminClientDetail> {
    const response = await api.get<any>(`/admin/clients/${id}`);
    return response.data.data;
  }

  /** Цілі клієнта: факт (збережені) і відтворення розрахунку калькуляторами з поясненнями (2026-09-30). */
  async getClientDailyNormsCalculation(id: number): Promise<AdminClientDailyNormsCalculation> {
    const response = await api.get<any>(`/admin/clients/${id}/daily-norms-calculation`);
    return response.data.data;
  }

  /** Перерахувати цілі клієнта за поточними даними й зберегти (затирає й ручні правки); повертає оновлений розрахунок. */
  async recalculateClientDailyNorms(id: number): Promise<AdminClientDailyNormsCalculation> {
    const response = await api.post<any>(`/admin/clients/${id}/daily-norms-calculation/recalculate`);
    return response.data.data;
  }

  /** Той самий розрахунок цілей у PDF - завантаження файлу. */
  async downloadClientDailyNormsCalculationPdf(id: number): Promise<void> {
    const response = await api.get(`/admin/clients/${id}/daily-norms-calculation/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-norms-calculation-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getExpertById(id: number): Promise<AdminExpertDetail> {
    const response = await api.get<any>(`/admin/experts/${id}`);
    return response.data.data;
  }

  async setBillingExempt(userId: number, exempt: boolean): Promise<void> {
    await api.patch(`/admin/client-payments/users/${userId}/billing-exempt`, { exempt });
  }

  async getClientPaymentsOptions(): Promise<{ plans: AdminPlanOption[]; clients: AdminClientOption[] }> {
    const response = await api.get<any>('/admin/client-payments/options');
    const data = response.data.data ?? response.data;
    return { plans: data?.plans ?? [], clients: data?.clients ?? [] };
  }

  async getExpertPaymentsOptions(): Promise<{ plans: AdminPlanOption[] }> {
    const response = await api.get<any>('/admin/expert-payments/options');
    const data = response.data.data ?? response.data;
    return { plans: data?.plans ?? [] };
  }

  async setClientPlan(userId: number, planId: number): Promise<void> {
    await api.patch(`/admin/client-payments/users/${userId}/plan`, { planId });
  }

  async createVirtualPayment(userId: number, payload: CreateVirtualPaymentPayload): Promise<VirtualPaymentResult> {
    const response = await api.post<any>(`/admin/client-payments/users/${userId}/virtual-payment`, payload);
    return response.data.data;
  }

  async resetClientSubscription(userId: number): Promise<void> {
    await api.post(`/admin/client-payments/users/${userId}/reset-subscription`);
  }

  async getClientSubscriptionSummary(userId: number): Promise<ClientSubscriptionSummary> {
    const response = await api.get<any>(`/admin/client-payments/users/${userId}/subscription-status`);
    return response.data.data;
  }

  async setExpertPlan(userId: number, planId: number): Promise<void> {
    await api.patch(`/admin/expert-payments/users/${userId}/plan`, { planId });
  }
}

/**
 * Розрахунок денних норм клієнта (2026-09-30, `GET admin/clients/:id/daily-norms-calculation`). Рядки, підписи й розбіжності формує
 * бекенд - єдине джерело для вкладки й PDF.
 */
export interface AdminClientDailyNormsCalculation {
  client: { id: number; name: string | null; email: string | null };
  /** Коли збережені цілі востаннє змінювались; null - цілей нема. */
  factUpdatedAt: string | null;
  hasFact: boolean;
  /** Факт (у клієнта) і розрахунок поруч; `mismatch` - розбіжність. */
  rows: { label: string; fact: number | null; calculated: number | null; mismatch: boolean }[];
  inputs: { key: string; label: string; value: string | number | null }[];
  /** Ланцюжок розрахунку одним списком - загальна таблиця на початку (з тих самих кроків, що й `sections`). */
  summary: { section: string; name: string; value: DailyNormsStepValue; basis: DailyNormsStepBasis | null }[];
  /** Кроки калькуляторів з поясненнями; `error` - бракує даних, застосовано запасні значення. */
  sections: {
    key: string;
    title: string;
    steps: { step: number; name: string; value: DailyNormsStepValue; formula: string; basis: DailyNormsStepBasis | null }[];
    error: string | null;
  }[];
}

/** Значення кроку: число, БЖВ у грамах або назва (схема харчування). */
export type DailyNormsStepValue = number | string | Record<string, number>;
/** Основа кроку: наука (джерела калькулятора) / правило Everyday (без зовнішнього джерела) / арифметика. */
export type DailyNormsStepBasis = { type: 'science' | 'everyday' | 'arithmetic'; source: string | null };

export const adminUsersService = new AdminUsersService();
